import type { ApplicationDetail } from "@/lib/api/review";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import type { AlipayDetails, PreVettingDraft, WeChatPayDetails } from "@chinese-vendor/schemas";

export interface FieldEntry {
  /** Whether this field is a file upload or a plain text value. */
  type: "text" | "upload";
  /** i18n key used as the label heading on the update form. */
  labelKey: TranslationKeys;
  /**
   * Extracts the current field value from a loaded ApplicationDetail.
   * For upload fields this returns the file URL; for text fields the raw value.
   */
  getValue: (detail: ApplicationDetail) => string | null | undefined;
  /**
   * For upload fields: returns a human-readable filename to display in
   * "Your Submission". Falls back to extracting the URL basename when the
   * data model has no dedicated fileName property.
   */
  getFileName?: (detail: ApplicationDetail) => string | null | undefined;
  /**
   * Returns a copy of the draft with this field patched to the given value.
   * Used by UpdateSubmissionScreen to persist the correction before resubmitting.
   */
  patchDraft: (draft: PreVettingDraft, value: string) => PreVettingDraft;
}

/** Extracts the filename portion from a URL path (e.g. "passport.pdf"). */
function basenameOf(url: string | null | undefined): string | null | undefined {
  if (!url) return url;
  try {
    return new URL(url).pathname.split("/").pop() ?? url;
  } catch {
    return url;
  }
}

/**
 * Maps each rejectable field key to its display label and value accessor.
 * Used by UpdateSubmissionScreen to avoid embedding data in URL params.
 */
export const FIELD_MAP: Record<string, FieldEntry> = {
  legal_representative_id_document: {
    type: "upload",
    labelKey: "preVetting.step1.idType",
    getValue: (d) => d.legalRepresentative?.idDocumentUrl,
    getFileName: (d) => basenameOf(d.legalRepresentative?.idDocumentUrl),
    patchDraft: (draft, value) => ({
      ...draft,
      step1: { ...draft.step1, legal_representative_id_document: value },
    }),
  },
  company_name_english: {
    type: "text",
    labelKey: "preVetting.step2.companyNameEnglish",
    getValue: (d) => d.businessInformation?.companyNameEnglish,
    patchDraft: (draft, value) => ({ ...draft, step2: { ...draft.step2, company_name_english: value } }),
  },
  unified_social_credit_code: {
    type: "text",
    labelKey: "preVetting.step2.unifiedSocialCreditCode",
    getValue: (d) => d.businessInformation?.ussc,
    patchDraft: (draft, value) => ({ ...draft, step2: { ...draft.step2, unified_social_credit_code: value } }),
  },
  business_license_document: {
    type: "upload",
    labelKey: "preVetting.step2.businessLicense",
    getValue: (d) => d.businessInformation?.businessLicenseUrl,
    getFileName: (d) => basenameOf(d.businessInformation?.businessLicenseUrl),
    patchDraft: (draft, value) => ({ ...draft, step2: { ...draft.step2, business_license_document: value } }),
  },
  iso_certification_document: {
    type: "upload",
    labelKey: "preVetting.step3.isoCertification",
    getValue: (d) => d.productCompliance?.isoCertificate?.fileUrl,
    getFileName: (d) => d.productCompliance?.isoCertificate?.fileName,
    patchDraft: (draft, value) => ({ ...draft, step3: { ...draft.step3, iso_certification_document: value } }),
  },
  ce_marking_document: {
    type: "upload",
    labelKey: "preVetting.step3.ceMarking",
    getValue: (d) => d.productCompliance?.ceMarking?.fileUrl,
    getFileName: (d) => d.productCompliance?.ceMarking?.fileName,
    patchDraft: (draft, value) => ({ ...draft, step3: { ...draft.step3, ce_marking_document: value } }),
  },
  quality_test_report_document: {
    type: "upload",
    labelKey: "preVetting.step3.qualityTestReport",
    getValue: (d) => d.productCompliance?.qualityTestReports?.[0]?.fileUrl,
    getFileName: (d) => d.productCompliance?.qualityTestReports?.[0]?.fileName,
    patchDraft: (draft, value) => ({
      ...draft,
      step3: {
        ...draft.step3,
        quality_test_report_document: value,
      },
    }),
  },
  eco_certification_document: {
    type: "upload",
    labelKey: "preVetting.step3.ecoCertification",
    getValue: (d) => d.productCompliance?.ecoCertifications?.[0]?.fileUrl,
    getFileName: (d) => d.productCompliance?.ecoCertifications?.[0]?.fileName,
    patchDraft: (draft, value) => ({
      ...draft,
      step3: {
        ...draft.step3,
        eco_certification_document: value,
      },
    }),
  },
  "step4.alipay_merchant_id": {
    type: "text",
    labelKey: "preVetting.step4.alipay.merchantId",
    getValue: (d) => d.financialSettlement?.alipay?.merchantId,
    patchDraft: (draft, value) => ({
      ...draft,
      step4: { ...draft.step4, alipay: { ...(draft.step4?.alipay as Partial<AlipayDetails> | undefined), alipay_merchant_id: value } as AlipayDetails },
    }),
  },
  "step4.wechatpay_merchant_id": {
    type: "text",
    labelKey: "preVetting.step4.wechatPay.merchantId",
    getValue: (d) => d.financialSettlement?.wechatPay?.merchantId,
    patchDraft: (draft, value) => ({
      ...draft,
      step4: { ...draft.step4, wechat_pay: { ...(draft.step4?.wechat_pay as Partial<WeChatPayDetails> | undefined), wechatpay_merchant_id: value } as WeChatPayDetails },
    }),
  },
  business_short_name_english: {
    type: "text",
    labelKey: "preVetting.step5.shortNameEnglish",
    getValue: (d) => d.brandingLocalization?.shortNameEnglish,
    patchDraft: (draft, value) => ({ ...draft, step5: { ...draft.step5, business_short_name_english: value } }),
  },
  business_description_english: {
    type: "text",
    labelKey: "preVetting.step5.descriptionEnglish",
    getValue: (d) => d.brandingLocalization?.descriptionEnglish,
    patchDraft: (draft, value) => ({ ...draft, step5: { ...draft.step5, business_description_english: value } }),
  },
  business_logo: {
    type: "upload",
    labelKey: "preVetting.step5.businessLogo",
    getValue: (d) => d.brandingLocalization?.logoUrl,
    getFileName: (d) => basenameOf(d.brandingLocalization?.logoUrl),
    patchDraft: (draft, value) => ({ ...draft, step5: { ...draft.step5, business_logo: value } }),
  },
};
